import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Slider } from '../../components/ui/slider';
import { buildReceiptHtml } from '../../lib/receipt';
import QRCode from 'qrcode';
import { processLogoUpload, buildPrintLogo, printableWidth } from '../../lib/logoProcessor';
import { eposTestPrint } from '../../lib/eposPrint';
import { receiptLabels } from '../../lib/receiptLabels';
import { Save, Upload, Trash2, ImageIcon, Search, Printer, Loader2, Send } from 'lucide-react';

const linesToText = (arr) => (arr || []).join('\n');
const textToLines = (txt) => (txt || '').split('\n').map((l) => l.trim()).filter(Boolean);

// Lingue disponibili per le intestazioni/etichette dello scontrino.
// Es. lo spagnolo mostra "CIF/NIF" al posto di "P.IVA", indipendentemente dalla lingua del sistema.
const RECEIPT_LANGS = [
  { value: 'it', label: 'Italiano', vat: 'P.IVA' },
  { value: 'es', label: 'Español', vat: 'CIF/NIF' },
  { value: 'en', label: 'English', vat: 'VAT' },
  { value: 'fr', label: 'Français', vat: 'TVA' },
  { value: 'pt', label: 'Português', vat: 'NIF' },
];

const cnPreview = (active) =>
  `flex-1 h-9 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`;

export default function GestoreReceipts() {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState([]);
  const [rid, setRid] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [processingLogo, setProcessingLogo] = useState(false);
  const [testingPrint, setTestingPrint] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [previewType, setPreviewType] = useState('scontrino'); // 'scontrino' | 'resoconto'
  const [search, setSearch] = useState('');
  const [sampleQr, setSampleQr] = useState('');
  const fileRef = useRef(null);
  const resizeTimer = useRef(null);

  useEffect(() => {
    // QR di esempio per l'anteprima dello scontrino fiscale (solo dimostrativo).
    QRCode.toDataURL('https://esempio.com/scontrino/anteprima', { margin: 1, width: 180 })
      .then(setSampleQr).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await adminApi.get('/admin/restaurants');
      setRestaurants(data);
      if (data.length && !rid) setRid(data[0].id);
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!rid) return;
    (async () => {
      const { data } = await adminApi.get(`/admin/restaurants/${rid}/receipt-template`);
      setRestaurant(data.restaurant);
      setTpl(data.template);
    })();
  }, [rid]);

  const set = (k, v) => setTpl((t) => ({ ...t, [k]: v }));

  const filteredRestaurants = restaurants.filter(
    (r) => (r.name || '').toLowerCase().includes(search.trim().toLowerCase())
  );

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error(t('receipts.imgTooBig')); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setProcessingLogo(true);
      try {
        // Ridimensiona e crea automaticamente il formato dedicato alla stampa.
        const { original, display, print, printWidth } = await processLogoUpload(reader.result, {
          paperWidth: Number(tpl?.paper_width) || 80,
        });
        setTpl((t) => ({
          ...t,
          logo_original_base64: original,
          logo_base64: display,
          logo_print_base64: print,
          logo_print_width: printWidth,
        }));
        toast.success(t('receipts.logoOptimized'));
      } catch (err) {
        setTpl((t) => ({ ...t, logo_base64: reader.result, logo_print_base64: reader.result }));
        toast.error(t('receipts.logoOptFailed'));
      } finally {
        setProcessingLogo(false);
        if (fileRef.current) fileRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  // Ridimensiona il logo di stampa alla larghezza scelta (con debounce).
  const onResizeLogo = (pct) => {
    const printableMax = printableWidth(Number(tpl?.paper_width) || 80);
    const width = Math.round((pct / 100) * printableMax);
    setTpl((t) => ({ ...t, logo_print_width: width }));
    const src = tpl?.logo_original_base64 || tpl?.logo_base64;
    if (!src) return;
    if (resizeTimer.current) clearTimeout(resizeTimer.current);
    setResizing(true);
    resizeTimer.current = setTimeout(async () => {
      try {
        const { dataUrl } = await buildPrintLogo(src, width);
        setTpl((t) => ({ ...t, logo_print_base64: dataUrl, logo_print_width: width }));
      } catch { /* ignora */ }
      finally { setResizing(false); }
    }, 220);
  };

  const removeLogo = () => setTpl((t) => ({
    ...t, logo_base64: null, logo_print_base64: null, logo_original_base64: null, logo_print_width: null,
  }));

  // Stampa di prova direttamente dal pannello Gestore (usa il logo in anteprima).
  const testPrint = async () => {
    if (!rid) return;
    setTestingPrint(true);
    try {
      const { data } = await adminApi.get(`/admin/restaurants/${rid}/print-settings`);
      const printer = data?.printer;
      if (!printer?.host) {
        toast.error(t('receipts.noPrinter'));
        return;
      }
      await eposTestPrint(printer, restaurant?.name || 'Stampa', tpl?.logo_print_base64);
      toast.success(t('receipts.testPrintSent'));
    } catch (e) {
      toast.error(e?.message || t('receipts.testPrintError'));
    } finally {
      setTestingPrint(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        logo_base64: tpl.logo_base64 || null,
        logo_print_base64: tpl.logo_print_base64 || null,
        logo_original_base64: tpl.logo_original_base64 || null,
        logo_print_width: tpl.logo_print_width || null,
        header_lines: tpl.header_lines || [],
        footer_lines: tpl.footer_lines || [],
        show_address: !!tpl.show_address,
        show_phone: !!tpl.show_phone,
        show_vat: !!tpl.show_vat,
        paper_width: Number(tpl.paper_width) || 80,
        vat_rate: Number(tpl.vat_rate) || 10,
        fiscal_title: tpl.fiscal_title || 'Fattura Semplificata',
        resoconto_title: tpl.resoconto_title || 'Resoconto (documento non fiscale)',
        receipt_language: tpl.receipt_language || null,
      };
      await adminApi.put(`/admin/restaurants/${rid}/receipt-template`, payload);
      toast.success(t('receipts.templateSaved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('receipts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const previewHtml = useMemo(() => {
    if (!tpl || !restaurant) return '';
    const sampleLines = [
      { name: 'Margherita', variant: 'Maxi', extras: [{ name: 'Bufala', price: 2.0 }], notes: '', qty: 1, unit_price: 9.5, line_total: 11.5 },
      { name: 'Spaghetti Carbonara', variant: null, extras: [], notes: 'Senza pepe', qty: 2, unit_price: 11.0, line_total: 22.0 },
      { name: 'Acqua Naturale 1L', variant: null, extras: [], notes: '', qty: 1, unit_price: 2.5, line_total: 2.5 },
    ];
    const items_total = 36.0;
    const total = items_total;
    const rate = Number(tpl.vat_rate || 10) / 100;
    const imponibile = Math.round((total / (1 + rate)) * 100) / 100;
    const iva = Math.round((total - imponibile) * 100) / 100;
    const isFiscal = previewType === 'scontrino';
    // La lingua dello scontrino può essere forzata dal Gestore, altrimenti segue l'attività.
    const lang = tpl.receipt_language || restaurant.language || 'it';
    const data = {
      is_fiscal: isFiscal,
      title: isFiscal ? (tpl.fiscal_title || 'Fattura Semplificata') : (tpl.resoconto_title || 'Resoconto (documento non fiscale)'),
      datetime: new Date().toISOString(),
      currency: 'EUR',
      lang,
      labels: receiptLabels(lang),
      template: tpl,
      restaurant,
      order: { table_name: 'Tavolo 5', covers: 2, waiter: 'Mario', created_at: new Date().toISOString() },
      lines: sampleLines,
      items_total, discount: 0, total,
      vat_rate: Number(tpl.vat_rate || 10), imponibile, iva,
      // Il QR (copia PDF) compare solo sullo scontrino fiscale, non sul resoconto.
      qr_base64: isFiscal ? sampleQr : undefined,
    };
    return buildReceiptHtml(data);
  }, [tpl, restaurant, previewType, sampleQr]);

  const Editor = (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="font-semibold mb-4 flex items-center gap-2"><ImageIcon className="h-4 w-4" /> {t('receipts.logo')}</div>
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-xl border border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/40 shrink-0">
            {processingLogo
              ? <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
              : (tpl?.logo_base64 ? <img src={tpl.logo_base64} alt="logo" className="max-h-full max-w-full object-contain" data-testid="receipt-logo-preview" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />)}
          </div>
          {/* Anteprima proporzionale: mostra il logo alla sua dimensione reale sulla carta */}
          {tpl?.logo_print_base64 && !processingLogo && (() => {
            const pmax = printableWidth(Number(tpl?.paper_width) || 80);
            const pct = Math.min(100, Math.round(((tpl?.logo_print_width || Math.round(pmax * 0.75)) / pmax) * 100));
            return (
              <div className="shrink-0 text-center">
                <div className="w-[150px] rounded-lg border border-border bg-white p-2 flex justify-center" data-testid="receipt-logo-paper-preview">
                  <img src={tpl.logo_print_base64} alt="anteprima stampa" style={{ width: `${pct}%` }} className="object-contain" data-testid="receipt-logo-print-preview" />
                </div>
                <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Printer className="h-3 w-3" /> {t('receipts.paperPreview')}</span>
              </div>
            );
          })()}
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="receipt-logo-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={processingLogo} data-testid="receipt-logo-upload-button"><Upload className="h-4 w-4 mr-1" /> {t('receipts.upload')}</Button>
            {tpl?.logo_base64 && <Button variant="ghost" size="sm" className="text-destructive" onClick={removeLogo} data-testid="receipt-logo-remove-button"><Trash2 className="h-4 w-4 mr-1" /> {t('receipts.remove')}</Button>}
          </div>
        </div>

        {/* Cursore di ridimensionamento del logo sullo scontrino, con anteprima live */}
        {tpl?.logo_base64 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="flex items-center gap-2">
                {t('receipts.logoSize')}
                {resizing && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              </Label>
              <span className="text-xs font-mono text-muted-foreground" data-testid="logo-size-value">
                {Math.round(((tpl?.logo_print_width || Math.round(printableWidth(Number(tpl?.paper_width) || 80) * 0.75)) / printableWidth(Number(tpl?.paper_width) || 80)) * 100)}%
              </span>
            </div>
            <Slider
              min={30}
              max={100}
              step={5}
              value={[Math.round(((tpl?.logo_print_width || Math.round(printableWidth(Number(tpl?.paper_width) || 80) * 0.75)) / printableWidth(Number(tpl?.paper_width) || 80)) * 100)]}
              onValueChange={(v) => onResizeLogo(v[0])}
              disabled={!tpl?.logo_original_base64 && !tpl?.logo_base64}
              className="py-2"
              data-testid="logo-size-slider"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('receipts.logoSizeHint')}
            </p>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={testPrint} disabled={testingPrint || processingLogo} data-testid="receipt-test-print-button">
            {testingPrint ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} {t('receipts.testPrint')}
          </Button>
          <span className="text-xs text-muted-foreground">{t('receipts.testPrintDesc')}</span>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          {t('receipts.logoAutoNote')}
        </p>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="font-semibold">{t('receipts.headerFooter')}</div>
        <div>
          <Label>{t('receipts.headerLines')}</Label>
          <Textarea value={linesToText(tpl?.header_lines)} onChange={(e) => set('header_lines', textToLines(e.target.value))} className="mt-1.5" rows={2} placeholder={t('receipts.oneLinePerLine')} data-testid="receipt-header-input" />
        </div>
        <div>
          <Label>{t('receipts.footerLines')}</Label>
          <Textarea value={linesToText(tpl?.footer_lines)} onChange={(e) => set('footer_lines', textToLines(e.target.value))} className="mt-1.5" rows={2} placeholder={t('receipts.footerPlaceholder')} data-testid="receipt-footer-input" />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="font-semibold">{t('receipts.fiscalData')}</div>
        <div className="text-xs text-muted-foreground -mt-2">{t('receipts.fiscalDataSource')}: {restaurant?.address || '—'} · {restaurant?.phone || '—'} · {restaurant?.vat || '—'}</div>
        <label className="flex items-center justify-between"><span className="text-sm">{t('receipts.showAddress')}</span><Switch checked={!!tpl?.show_address} onCheckedChange={(v) => set('show_address', v)} data-testid="receipt-toggle-address" /></label>
        <label className="flex items-center justify-between"><span className="text-sm">{t('receipts.showPhone')}</span><Switch checked={!!tpl?.show_phone} onCheckedChange={(v) => set('show_phone', v)} /></label>
        <label className="flex items-center justify-between"><span className="text-sm">{t('receipts.showVat')}</span><Switch checked={!!tpl?.show_vat} onCheckedChange={(v) => set('show_vat', v)} /></label>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="font-semibold">{t('receipts.formatFiscal')}</div>
        <div>
          <Label>{t('receipts.receiptLanguage')}</Label>
          <Select
            value={tpl?.receipt_language || restaurant?.language || 'it'}
            onValueChange={(v) => set('receipt_language', v)}
          >
            <SelectTrigger className="mt-1.5" data-testid="receipt-language-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RECEIPT_LANGS.map((l) => (
                <SelectItem key={l.value} value={l.value} data-testid={`receipt-language-option-${l.value}`}>
                  {l.label} — {l.vat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">{t('receipts.receiptLanguageHint')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>{t('receipts.paperWidth')}</Label>
            <Select value={String(tpl?.paper_width)} onValueChange={(v) => set('paper_width', Number(v))}>
              <SelectTrigger className="mt-1.5" data-testid="receipt-width-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="58">58 mm</SelectItem>
                <SelectItem value="80">80 mm</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('receipts.vatIncl')}</Label>
            <Input type="number" value={tpl?.vat_rate} onChange={(e) => set('vat_rate', e.target.value)} className="mt-1.5" data-testid="receipt-vat-input" />
          </div>
        </div>
        <div>
          <Label>{t('receipts.fiscalTitle')}</Label>
          <Input value={tpl?.fiscal_title} onChange={(e) => set('fiscal_title', e.target.value)} className="mt-1.5" data-testid="receipt-fiscal-title-input" />
        </div>
        <div>
          <Label>{t('receipts.resocontoTitle')}</Label>
          <Input value={tpl?.resoconto_title} onChange={(e) => set('resoconto_title', e.target.value)} className="mt-1.5" />
        </div>
      </Card>
    </div>
  );

  const Preview = (
    <div>
      <div className="flex items-center gap-1 p-1 rounded-xl bg-muted mb-3" data-testid="receipt-preview-switch">
        <button onClick={() => setPreviewType('scontrino')} data-testid="preview-type-fiscale" className={cnPreview(previewType === 'scontrino')}>{t('receipts.fiscal')}</button>
        <button onClick={() => setPreviewType('resoconto')} data-testid="preview-type-resoconto" className={cnPreview(previewType === 'resoconto')}>{t('receipts.resoconto')}</button>
      </div>
      <div className="text-sm font-medium text-muted-foreground mb-2">
        {previewType === 'scontrino' ? t('receipts.previewFiscal') : t('receipts.previewResoconto')}
      </div>
      <div className="rounded-2xl border border-border bg-muted/40 p-4 flex justify-center">
        <iframe
          title="anteprima-scontrino"
          srcDoc={previewHtml}
          data-testid="gestore-receipts-preview-iframe"
          className="bg-white shadow-sm rounded-md w-full max-w-[320px]"
          style={{ height: 560, border: 'none' }}
        />
      </div>
    </div>
  );

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 pb-24 md:pb-6">
      <GestorePageHeader title={t('receipts.title')} subtitle={t('receipts.subtitle')}>
        <Button onClick={save} disabled={saving || !tpl} className="rounded-xl hidden md:inline-flex" data-testid="gestore-receipts-save-button"><Save className="h-4 w-4 mr-2" /> {saving ? t('receipts.saving') : t('common.save')}</Button>
      </GestorePageHeader>

      <div className="mb-4 max-w-xl">
        <Label>{t('receipts.business')}</Label>
        <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('receipts.searchByName')} className="pl-9" data-testid="receipt-search-input" />
          </div>
          <Select value={rid} onValueChange={setRid}>
            <SelectTrigger className="sm:w-[260px]" data-testid="gestore-receipts-restaurant-select"><SelectValue placeholder={t('receipts.selectBusiness')} /></SelectTrigger>
            <SelectContent>
              {filteredRestaurants.length === 0 && <SelectItem value="__none" disabled>{t('receipts.noResults')}</SelectItem>}
              {filteredRestaurants.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {tpl && restaurant && (
        <>
          {/* Desktop split view */}
          <div className="hidden md:grid md:grid-cols-[1fr_380px] gap-6">
            {Editor}
            <div className="sticky top-6 self-start">{Preview}</div>
          </div>

          {/* Mobile tabs */}
          <div className="md:hidden">
            <Tabs defaultValue="editor" data-testid="gestore-receipts-tabs">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="editor" data-testid="receipts-tab-editor">{t('receipts.editor')}</TabsTrigger>
                <TabsTrigger value="preview" data-testid="receipts-tab-preview">{t('receipts.preview')}</TabsTrigger>
              </TabsList>
              <TabsContent value="editor" className="mt-4">{Editor}</TabsContent>
              <TabsContent value="preview" className="mt-4">{Preview}</TabsContent>
            </Tabs>
          </div>

          {/* Mobile sticky save bar */}
          <div className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-background/90 backdrop-blur p-3">
            <Button onClick={save} disabled={saving} className="w-full rounded-xl h-12" data-testid="gestore-receipts-save-button-mobile">
              <Save className="h-4 w-4 mr-2" /> {saving ? t('receipts.saving') : t('common.save')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
